import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { blockUrlWithAudience } from '@/lib/utils';

import type {
  BlockWithAudience,
  BodyBlock,
  HeadingWithStyles,
  PageData,
  ProgrammeType,
} from '@/types/shared';

export interface ThankYouProps extends BlockWithAudience {
  thankYouMsgHeading?: HeadingWithStyles;
  thankYouMsgBody?: BodyBlock;
  downloadFile?: string;
  thankYouCTALabel?: string;
  thankYouCTAHrefExternal?: string;
  thankYouCTAHrefInternal?: PageData | ProgrammeType;
}

const Thankyou = ({
  thankYouMsgHeading,
  thankYouMsgBody,
  downloadFile,
  thankYouCTALabel,
  thankYouCTAHrefExternal,
  thankYouCTAHrefInternal,
  pageAudience,
  isAudienceSwitcherEnabled,
}: ThankYouProps) => {
  const buttonInternalURL = thankYouCTAHrefInternal?.slug.current;

  return (
    <Container>
      <StyledCaptionHeadingDescription
        heading={thankYouMsgHeading}
        description={thankYouMsgBody}
        padding={{ description: 'medium', descriptionMobile: 'medium' }}
        pageAudience={pageAudience}
        isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
      />
      {thankYouCTALabel &&
        thankYouCTAHrefExternal &&
        !downloadFile &&
        !buttonInternalURL && (
          <ButtonLink variant="flat-light" href={thankYouCTAHrefExternal}>
            {thankYouCTALabel}
          </ButtonLink>
        )}
      {thankYouCTALabel && buttonInternalURL && !downloadFile && (
        <ButtonLink
          href={blockUrlWithAudience(
            buttonInternalURL,
            thankYouCTAHrefInternal.audienceRef,
            pageAudience,
            false,
            isAudienceSwitcherEnabled,
          )}
          variant="flat-light"
        >
          {thankYouCTALabel}
        </ButtonLink>
      )}
      {thankYouCTALabel && downloadFile && (
        <ButtonLink href={downloadFile} variant="flat-light">
          {thankYouCTALabel}
        </ButtonLink>
      )}
    </Container>
  );
};

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-150);
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: inherit;
  height: 100%;
  width: 100%;
  color: var(--text-strong);
  justify-content: center;
  align-items: flex-start;
  padding: 0 var(--spacing-300);

  ${screen.md} {
    padding: 0 var(--spacing-500);
  }

  a {
    margin-top: var(--spacing-500);
  }
`;

const ButtonLink = styled(Button)`
  line-height: normal;
`;

export { Thankyou };
